import React from 'react';
import { useTranslation } from 'react-i18next';
import { Check, X, Trash2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const FlashDealsTab = ({ 
  settings, 
  updateSetting,
  productSearch,
  setProductSearch,
  searchProducts,
  searchResults,
  setSearchResults,
  selectedProducts,
  setSelectedProducts,
  allProducts,
  setHasChanges
}) => {
  const { t } = useTranslation();

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>{t('homepageManagement.flashDeals.title')}</CardTitle>
            <CardDescription>{t('homepageManagement.flashDeals.description')}</CardDescription>
          </div>
          <Switch
            checked={settings.flash_deals_enabled}
            onCheckedChange={(v) => updateSetting('flash_deals_enabled', v)}
          />
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Başlıklar */}
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>{t('homepageManagement.flashDeals.sectionTitle')}</Label>
            <Input
              value={settings.flash_deals_title}
              onChange={(e) => updateSetting('flash_deals_title', e.target.value)}
              placeholder={t('home.flashDeals')}
            />
          </div>
          <div className="space-y-2">
            <Label>{t('homepageManagement.flashDeals.sectionSubtitle')}</Label>
            <Input
              value={settings.flash_deals_subtitle}
              onChange={(e) => updateSetting('flash_deals_subtitle', e.target.value)}
              placeholder={t('home.limitedTimeOffers')}
            />
          </div>
        </div>

        {/* Ürün Görünümü Ayarları */}
        <div className="border rounded-lg p-4 space-y-4">
          <h4 className="font-semibold text-sm">{t('homepageManagement.flashDeals.productView')}</h4>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t('homepageManagement.flashDeals.productCount')}</Label>
              <Input
                type="number"
                min="6"
                max="24"
                value={settings.flash_deals_product_count || 12}
                onChange={(e) => updateSetting('flash_deals_product_count', parseInt(e.target.value))}
              />
              <p className="text-xs text-muted-foreground">{t('homepageManagement.flashDeals.productCountRange')}</p>
            </div>
            <div className="space-y-2">
              <Label>{t('homepageManagement.flashDeals.rowCount')}</Label>
              <Select
                value={String(settings.flash_deals_rows || 2)}
                onValueChange={(v) => updateSetting('flash_deals_rows', parseInt(v))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">{t('homepageManagement.flashDeals.row1')}</SelectItem>
                  <SelectItem value="2">{t('homepageManagement.flashDeals.row2')}</SelectItem>
                  <SelectItem value="3">{t('homepageManagement.flashDeals.row3')}</SelectItem>
                  <SelectItem value="4">{t('homepageManagement.flashDeals.row4')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Geri Sayım Ayarları */}
        <div className="border rounded-lg p-4 space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-semibold text-sm">{t('homepageManagement.flashDeals.countdown')}</h4>
            <Switch
              checked={settings.flash_deals_countdown_enabled}
              onCheckedChange={(v) => updateSetting('flash_deals_countdown_enabled', v)}
            />
          </div>
          {settings.flash_deals_countdown_enabled && (
            <div className="space-y-2">
              <Label>{t('homepageManagement.flashDeals.countdownHours')}</Label>
              <Input
                type="number"
                min="1"
                max="24"
                value={settings.flash_deals_countdown_hours || 5}
                onChange={(e) => updateSetting('flash_deals_countdown_hours', parseInt(e.target.value))}
              />
            </div>
          )}
        </div>

        {/* Görsel Öğeler */}
        <div className="border rounded-lg p-4 space-y-4">
          <h4 className="font-semibold text-sm">{t('homepageManagement.flashDeals.visualElements')}</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <Label className="text-sm">{t('homepageManagement.flashDeals.discountBadge')}</Label>
                <p className="text-xs text-muted-foreground">{t('homepageManagement.flashDeals.discountBadgeDesc')}</p>
              </div>
              <Switch
                checked={settings.flash_deals_show_discount_badge !== false}
                onCheckedChange={(v) => updateSetting('flash_deals_show_discount_badge', v)}
              />
            </div>
            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <Label className="text-sm">{t('homepageManagement.flashDeals.categoryBadge')}</Label>
                <p className="text-xs text-muted-foreground">{t('homepageManagement.flashDeals.categoryBadgeDesc')}</p>
              </div>
              <Switch
                checked={settings.flash_deals_show_category_badge === true}
                onCheckedChange={(v) => updateSetting('flash_deals_show_category_badge', v)}
              />
            </div>
            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <Label className="text-sm">{t('homepageManagement.flashDeals.turkeyBadge')}</Label>
                <p className="text-xs text-muted-foreground">{t('homepageManagement.flashDeals.turkeyBadgeDesc')}</p>
              </div>
              <Switch
                checked={settings.flash_deals_show_made_in_turkey !== false}
                onCheckedChange={(v) => updateSetting('flash_deals_show_made_in_turkey', v)}
              />
            </div>
            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <Label className="text-sm">{t('homepageManagement.flashDeals.heartButton')}</Label>
                <p className="text-xs text-muted-foreground">{t('homepageManagement.flashDeals.heartButtonDesc')}</p>
              </div>
              <Switch
                checked={settings.flash_deals_show_heart_button !== false}
                onCheckedChange={(v) => updateSetting('flash_deals_show_heart_button', v)}
              />
            </div>
          </div>
        </div>

        <p className="text-sm text-muted-foreground">
          {t('homepageManagement.flashDeals.note')}
        </p>

        {/* Manuel Ürün Seçimi */}
        <div className="border rounded-lg p-4 space-y-4 mt-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-semibold text-sm">{t('homepageManagement.flashDeals.manualProducts') || 'Manuel Ürün Seçimi'}</h4>
              <p className="text-xs text-muted-foreground">
                {t('homepageManagement.flashDeals.manualProductsDesc') || 'Ana sayfada gösterilecek ürünleri manuel olarak seçin'}
              </p>
            </div>
            <Badge variant={selectedProducts.length > 0 ? "default" : "secondary"}>
              {selectedProducts.length} {t('homepageManagement.flashDeals.selected') || 'ürün seçili'}
            </Badge>
          </div>

          {/* Ürün Arama */}
          <div className="space-y-2">
            <Label>{t('homepageManagement.flashDeals.searchProduct') || 'Ürün Ara'}</Label>
            <div className="relative">
              <Input
                placeholder={t('homepageManagement.flashDeals.searchPlaceholder') || 'Ürün adı veya kategori...'}
                value={productSearch}
                onChange={(e) => {
                  setProductSearch(e.target.value);
                  searchProducts(e.target.value);
                }}
              />
              {searchResults.length > 0 && (
                <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {searchResults.map((product) => (
                    <button
                      key={product.id}
                      className={`w-full p-3 text-left hover:bg-gray-50 flex items-center gap-3 border-b last:border-b-0 ${
                        selectedProducts.includes(product.id) ? 'bg-green-50' : ''
                      }`}
                      onClick={() => {
                        if (!selectedProducts.includes(product.id)) {
                          setSelectedProducts([...selectedProducts, product.id]);
                          setHasChanges(true);
                        }
                        setProductSearch('');
                        setSearchResults([]);
                      }}
                    >
                      <img 
                        src={product.images?.[0] || '/placeholder.jpg'} 
                        alt={product.name}
                        className="w-10 h-10 rounded object-cover"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{product.name}</p>
                        <p className="text-xs text-muted-foreground">{product.category} • {product.price} TL</p>
                      </div>
                      {selectedProducts.includes(product.id) && (
                        <Check className="w-4 h-4 text-green-600" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Seçili Ürünler */}
          {selectedProducts.length > 0 && (
            <div className="space-y-2">
              <Label>{t('homepageManagement.flashDeals.selectedProducts') || 'Seçili Ürünler'}</Label>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
                {selectedProducts.map((productId, index) => {
                  const product = allProducts.find(p => p.id === productId);
                  if (!product) return null;
                  return (
                    <div 
                      key={productId} 
                      className="relative border rounded-lg p-2 group"
                    >
                      <div className="absolute -top-2 -left-2 w-5 h-5 bg-red-600 text-white rounded-full flex items-center justify-center text-xs font-bold">
                        {index + 1}
                      </div>
                      <button
                        className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() => {
                          setSelectedProducts(selectedProducts.filter(id => id !== productId));
                          setHasChanges(true);
                        }}
                      >
                        <X className="w-3 h-3" />
                      </button>
                      <img 
                        src={product.images?.[0] || '/placeholder.jpg'} 
                        alt={product.name}
                        className="w-full aspect-square rounded object-cover mb-1"
                      />
                      <p className="text-xs font-medium truncate">{product.name}</p>
                      <p className="text-xs text-muted-foreground">{product.price} TL</p>
                    </div>
                  );
                })}
              </div>
              <div className="flex gap-2 mt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSelectedProducts([]);
                    setHasChanges(true);
                  }}
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  {t('homepageManagement.flashDeals.clearAll') || 'Tümünü Temizle'}
                </Button>
              </div>
            </div>
          )}

          {selectedProducts.length === 0 && (
            <p className="text-sm text-muted-foreground italic">
              {t('homepageManagement.flashDeals.autoSelectInfo') || 'Ürün seçilmediğinde her kategoriden otomatik ürün seçilir.'}
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default FlashDealsTab;
